// The Admin configuration console's area registry (lane B10 S20a; plan D14; PARALLEL-MAP §2.1 — the signatures are
// frozen for S20b). An area is one entry of the console's rail: his word, its place in the rail, the configuration kinds
// it lists and the panel that draws one key of it. S20a registers the five list areas from ./register.ts; S20b
// registers Thresholds, Reminders and Templates from views/b10/system/register.ts. Rail order is D14's — Statuses 10 ·
// Reason lists 20 · Taxonomies 30 · Thresholds 40 · Reminders 50 · Templates 60 · Segments 70 · Strategies 80.
//
// `kinds` names configuration kinds (`reasons`: every `reasons:<name>` key) or exact keys (`taxonomies:segment`). An
// exact key belongs to the area that names it, never to an area that names only its kind, so Segments and Strategies are
// two areas although both are taxonomies. An exact key is listed even before it has a first version, so the console can
// propose one. Pure module: no React, no tool call, so the node suite loads it as it is.

import type { ComponentType } from "react";

export interface ConsoleArea {
  /** The route segment: `#settings/config/<key>`. */
  key: string;
  /** His word for the area, from a labels module. */
  label: string;
  /** The place in the rail (D14's order). */
  order: number;
  /** The configuration kinds it lists, or exact keys `kind:name`. */
  kinds: readonly string[];
  /** The panel for one configuration key of the area. */
  Editor: ComponentType<{ kind: string; configKey: string }>;
}

const AREA_KEY = /^[a-z][a-z0-9_]*$/;
const KIND_OR_KEY = /^[a-z][a-z0-9_]*(:[a-z0-9_.]+)?$/;

const areas = new Map<string, ConsoleArea>();

/** Adds an area to the rail. A duplicate key, an exact key another area already names, or an empty area throws. */
export function registerConsoleArea(area: ConsoleArea): void {
  if (!AREA_KEY.test(area.key)) throw new Error(`registerConsoleArea: ${area.key} is not an area key`);
  if (areas.has(area.key)) throw new Error(`registerConsoleArea: ${area.key} is already registered`);
  if (!area.label.trim()) throw new Error(`registerConsoleArea: ${area.key} has no label`);
  if (area.kinds.length === 0) throw new Error(`registerConsoleArea: ${area.key} lists no kind`);
  for (const entry of area.kinds) {
    if (!KIND_OR_KEY.test(entry)) throw new Error(`registerConsoleArea: ${entry} is not a kind or a key`);
    const other = [...areas.values()].find((a) => a.kinds.includes(entry));
    if (other) throw new Error(`registerConsoleArea: ${entry} is already listed by ${other.key}`);
  }
  areas.set(area.key, Object.freeze({ ...area, kinds: Object.freeze([...area.kinds]) }));
}

/** Every area, in rail order (then by key). */
export function consoleAreas(): readonly ConsoleArea[] {
  return [...areas.values()].sort((a, b) => a.order - b.order || a.key.localeCompare(b.key));
}

/** The area registered under a route segment. */
export function consoleArea(key: string): ConsoleArea | undefined {
  return areas.get(key);
}

/** `reasons` for `reasons:withdrawal`; a bare key (`sla_thresholds`) is its own kind. */
export function kindOf(configKey: string): string {
  return configKey.split(":", 1)[0] ?? configKey;
}

/** `withdrawal` for `reasons:withdrawal`; a bare key is its own name. */
export function nameOf(configKey: string): string {
  const at = configKey.indexOf(":");
  return at < 0 ? configKey : configKey.slice(at + 1);
}

/** The area a configuration key is drawn in: the area naming the exact key, else the area naming its kind. */
export function areaOf(configKey: string, among: readonly ConsoleArea[] = consoleAreas()): ConsoleArea | undefined {
  return among.find((a) => a.kinds.includes(configKey)) ?? among.find((a) => a.kinds.includes(kindOf(configKey)));
}

/** The keys an area lists: every key of `known` (the keys with a version) the area holds, by name, then the exact keys
 * it names that have no version yet, in the order it names them (so a first version can be proposed). A key another
 * area names exactly is left out. */
export function keysOf(area: ConsoleArea, known: readonly string[], among: readonly ConsoleArea[] = consoleAreas()): string[] {
  const published = [...new Set(known)]
    .filter((key) => areaOf(key, among)?.key === area.key)
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
  const unpublished = area.kinds.filter((entry) => entry.includes(":") && !published.includes(entry));
  return [...published, ...unpublished];
}

/** The console's route for an area, or for one key of it: `#settings/config/<area>[/<name>]`. */
export function consoleHref(areaKey: string, configKey?: string): string {
  return configKey === undefined ? `#settings/config/${areaKey}` : `#settings/config/${areaKey}/${nameOf(configKey)}`;
}

/** The area and key name a console route asks for: `["config", "reason_lists", "withdrawal"]` (the route's parts). */
export function parseConsoleRoute(parts: readonly string[]): { area?: string; name?: string } {
  if (parts[0] !== "config") return {};
  const [, area, name] = parts;
  return { ...(area ? { area } : {}), ...(name ? { name } : {}) };
}

/** The key of `keys` a route's name means, or undefined. */
export function keyFromRoute(name: string | undefined, keys: readonly string[]): string | undefined {
  return name === undefined ? undefined : keys.find((key) => nameOf(key) === name);
}
